/**
 * Track Questions Tab — שאלות משתמשים
 *
 * The training loop: read what people actually asked in this area, spot the
 * questions the AI answers badly, and turn them into a rule with one click.
 *
 * PSEUDONYMOUS BY DESIGN. The server never sends who asked — only a short stable
 * reference, so repeat askers are visible without anyone being identified. That
 * is what keeps this a training screen rather than a personal-data screen.
 */

import { useEffect, useState } from 'react'
import { AlertCircle, MessageCircle, Search, Sparkles } from 'lucide-react'
import { useAdminStore } from '../../store/adminStore'
import api, { handleApiError } from '../../services/api'
import type { TrackKey } from '../../config/tracks'

interface TrackQuestionsTabProps {
  track: TrackKey
}

const DAY_OPTIONS = [
  { value: 7, label: '7 ימים' },
  { value: 30, label: '30 ימים' },
  { value: 90, label: '90 ימים' },
  { value: 365, label: 'שנה' },
]

export default function TrackQuestionsTab({ track }: TrackQuestionsTabProps) {
  const { trackQuestions, trackQuestionsLoading, trackQuestionsError, fetchTrackQuestions } =
    useAdminStore()

  const [days, setDays] = useState(30)
  const [search, setSearch] = useState('')
  const [ruleFor, setRuleFor] = useState<string | null>(null)
  const [ruleTitle, setRuleTitle] = useState('')
  const [ruleContent, setRuleContent] = useState('')
  const [ruleSaving, setRuleSaving] = useState(false)
  const [ruleError, setRuleError] = useState<string | null>(null)
  const [ruleSaved, setRuleSaved] = useState(false)

  useEffect(() => {
    void fetchTrackQuestions(track, { days })
  }, [fetchTrackQuestions, track, days])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    void fetchTrackQuestions(track, { days, search: search.trim() || undefined })
  }

  const openRuleForm = (question: string) => {
    setRuleFor(question)
    setRuleTitle(question.slice(0, 80))
    setRuleContent('')
    setRuleError(null)
    setRuleSaved(false)
  }

  const saveRule = async () => {
    if (!ruleTitle.trim() || !ruleContent.trim()) {
      setRuleError('נדרשים כותרת ותוכן')
      return
    }
    setRuleSaving(true)
    setRuleError(null)
    try {
      await api.post('/admin/advisor-rules', {
        // The scope is what keeps a mortgage correction out of the retirement and
        // advisory prompts.
        scope: track,
        category: 'general',
        title: ruleTitle.trim(),
        content: ruleContent.trim(),
        priority: 'mandatory',
      })
      setRuleSaved(true)
      setRuleFor(null)
    } catch (error) {
      setRuleError(handleApiError(error))
    } finally {
      setRuleSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-900">
        <p>
          כאן רואים מה משתמשים שאלו בפועל באזור הזה. אם תשובה של ה-AI לא מדויקת — אפשר
          להפוך את השאלה לכלל, וה-AI יתייחס אליו בכל שיחה עתידית באזור הזה בלבד, בלי
          פריסה מחדש.
        </p>
        <p className="mt-1">השאלות מוצגות ללא זיהוי המשתמש.</p>
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="q-days" className="mb-1 block text-xs font-medium text-gray-700">
            טווח זמן
          </label>
          <select
            id="q-days"
            className="input"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {DAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <label htmlFor="q-search" className="mb-1 block text-xs font-medium text-gray-700">
            חיפוש בטקסט השאלה
          </label>
          <input
            id="q-search"
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="למשל: מקדם המרה"
          />
        </div>
        <button type="submit" className="btn btn-secondary">
          <Search size={15} aria-hidden="true" className="ms-1" />
          חיפוש
        </button>
      </form>

      {trackQuestionsError && (
        <div
          className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle size={16} aria-hidden="true" />
          {trackQuestionsError}
        </div>
      )}

      {ruleSaved && (
        <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800" role="status">
          הכלל נוצר. הוא ייכנס לתוקף בשיחה הבאה באזור זה.
        </div>
      )}

      {trackQuestionsLoading && <p className="text-sm text-gray-600">טוען שאלות...</p>}

      {!trackQuestionsLoading && trackQuestions.length === 0 && (
        <p className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-700">
          עדיין לא נשאלו שאלות באזור הזה בטווח שנבחר.
        </p>
      )}

      <ul className="space-y-3">
        {trackQuestions.map((row, i) => (
          <li key={`${row.conversationId}-${i}`} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm text-gray-900">{row.question}</p>
                <p className="mt-2 text-xs text-gray-500">
                  {row.userRef}
                  {row.topicSlug ? ` · נושא: ${row.topicSlug}` : ' · שיחה כללית'} ·{' '}
                  {new Date(row.askedAt).toLocaleDateString('he-IL')}
                  {row.flagged && (
                    <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-amber-900">
                      סומן לבדיקה
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => openRuleForm(row.question)}
                className="btn btn-secondary whitespace-nowrap"
              >
                <Sparkles size={14} aria-hidden="true" className="ms-1" />
                הפוך לכלל
              </button>
            </div>

            {ruleFor === row.question && (
              <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50/50 p-4">
                <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900">
                  <MessageCircle size={15} aria-hidden="true" />
                  כלל חדש לאזור זה
                </h4>

                <label htmlFor="rule-title" className="mt-3 mb-1 block text-xs font-medium text-gray-700">
                  כותרת
                </label>
                <input
                  id="rule-title"
                  className="input"
                  value={ruleTitle}
                  onChange={(e) => setRuleTitle(e.target.value)}
                />

                <label htmlFor="rule-content" className="mt-3 mb-1 block text-xs font-medium text-gray-700">
                  ההנחיה ל-AI
                </label>
                <textarea
                  id="rule-content"
                  className="input min-h-[100px]"
                  value={ruleContent}
                  onChange={(e) => setRuleContent(e.target.value)}
                  placeholder="לדוגמה: כששואלים על מקדם המרה — תמיד להסביר שהמקדם תלוי בגיל הפרישה ובתוחלת החיים, ולציין שבפוליסות ותיקות ייתכן מקדם מובטח."
                />

                {ruleError && <p className="mt-2 text-sm text-red-700">{ruleError}</p>}

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={saveRule}
                    disabled={ruleSaving}
                    className="btn btn-primary"
                  >
                    {ruleSaving ? 'שומר...' : 'שמירת הכלל'}
                  </button>
                  <button type="button" onClick={() => setRuleFor(null)} className="btn btn-secondary">
                    ביטול
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
